import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/auth.store.js';
import { useGameStore } from '../store/game.store.js';
import { BackgroundEffects } from '../components/lobby/BackgroundEffects.js';
import { WinnerCard } from '../components/results/WinnerCard.js';
import { PlayerStats } from '../components/results/PlayerStats.js';
import { GameSummary } from '../components/results/GameSummary.js';
import { ActionButtons } from '../components/results/ActionButtons.js';
import { useRoomStore } from '../store/room.store.js';

export function GameResultsScreen() {
  const { user, isSessionChecked } = useAuthStore();
  const navigate = useNavigate();

  const {
    calledNumbers,
    prizes,
    leaderboard,
    gameTimer,
    markedNumbers,
    startGame,
  } = useGameStore();

  // Route protection
  useEffect(() => {
    if (isSessionChecked && !user) {
      navigate('/login');
    }
  }, [user, isSessionChecked, navigate]);

  const { players } = useRoomStore();

  // Determine if the current user is host in the Colyseus game room
  const currentPlayer = players.find((p) => p.displayName === user?.displayName);
  const isHost = currentPlayer ? currentPlayer.isHost : false;

  const handlePlayAgain = () => {
    // Restart match loops and redirect to Waiting Room
    startGame();
    navigate('/waiting-room');
  };

  const handleReturnHome = () => {
    navigate('/home');
  };

  // Compile performance outcomes
  const totalCalledCount = calledNumbers.length;
  
  // Mock performance parameters
  const userLeaderboardItem = leaderboard.find((p) => p.name === (user ? user.displayName : ''));
  const userRank = userLeaderboardItem ? userLeaderboardItem.rank : 3;
  const userPrizeClaims = userLeaderboardItem ? userLeaderboardItem.prizesClaimed : 0;
  
  // Count matching ticket cells marked
  const userMatchedCount = markedNumbers.length;
  const completionPercent = Math.min(Math.round((userMatchedCount / 15) * 100), 100);

  // Formulate text for clipboard sharing
  const shareText = `🏆 Tambola Online Match Finished! 🏆\n` +
    `👤 Player Name: ${user ? user.displayName : 'Player'}\n` +
    `🏅 Personal Rank: #${userRank}\n` +
    `🎁 Claims Won: ${userPrizeClaims} prize(s)\n` +
    `🎱 Numbers Called: ${totalCalledCount} balls\n` +
    `🎯 Completion Progress: ${completionPercent}%\n` +
    `Play online now at Tambola Multiplayer Housie!`;

  return (
    <div className="relative flex min-h-[100dvh] w-full flex-col pb-24 overflow-y-auto scrollbar-thin">
      {/* 3D background grids & lighting */}
      <BackgroundEffects />

      {/* Confetti falling overlay */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        {Array.from({ length: 30 }, (_, i) => {
          const colors = ['#a855f7', '#ec4899', '#3b82f6', '#10b981', '#f59e0b'];
          const randomColor = colors[i % colors.length];
          const startX = Math.random() * 100;
          const duration = Math.random() * 2.5 + 2;

          return (
            <motion.div
              key={i}
              initial={{ y: -20, x: `${startX}vw`, opacity: 1, rotate: 0 }}
              animate={{ y: '100vh', opacity: 0, rotate: 360 }}
              transition={{
                duration,
                ease: 'linear',
                repeat: Infinity,
              }}
              style={{
                backgroundColor: randomColor,
                boxShadow: `0 0 6px ${randomColor}`,
              }}
              className="absolute w-2 h-3.5 rounded-sm"
            />
          );
        })}
      </div>

      {/* Header bar */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full text-center py-5 backdrop-blur-md border-b border-white/5 bg-slate-950/20 z-30"
      >
        <span className="text-[10px] font-black uppercase tracking-widest text-amber-400">
          Game Finished
        </span>
        <h2 className="font-display text-2xl font-black text-white uppercase tracking-wider mt-0.5">
          Congratulations!
        </h2>
      </motion.header>

      {/* Main grids summary */}
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 px-8 py-4 z-20 overflow-y-auto items-start lg:max-h-[calc(100vh-160px)] pb-10">
        
        {/* Left Column: Winners lists summary */}
        <div className="glass rounded-3xl p-5 border border-white/10 shadow-glow-lg flex flex-col justify-start h-full text-left overflow-hidden max-h-[280px] lg:max-h-full">
          <h3 className="font-display text-sm font-black tracking-wider text-amber-300 uppercase mb-4">
            Prize Winners
          </h3>
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {Object.entries(prizes).map(([name, detail], idx) => (
              <WinnerCard
                key={name}
                prizeName={name}
                winnerName={detail.winner || 'No Claims'}
                delayIndex={idx}
              />
            ))}
          </div>
        </div>

        {/* Center Column: All Players Leaderboard */}
        <div className="glass rounded-3xl p-5 border border-white/10 shadow-glow-lg flex flex-col justify-start h-full text-left overflow-hidden max-h-[280px] lg:max-h-full">
          <h3 className="font-display text-sm font-black tracking-wider text-amber-300 uppercase mb-4 flex justify-between items-center">
            <span>All Players Leaderboard</span>
            <span className="text-[10px] text-surface-400 font-semibold font-mono tracking-normal">
              {leaderboard.length} Players
            </span>
          </h3>
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {leaderboard.map((player) => {
              const isMe = player.name === user?.displayName;
              const isWinner = player.rank === 1 && player.score > 0;
              return (
                <motion.div
                  key={player.name}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                    isMe
                      ? 'bg-purple-600/20 border-purple-500/40 shadow-glow-sm'
                      : 'bg-slate-950/30 border-white/5 hover:border-white/10'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {/* Rank Badge */}
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black font-mono ${
                      player.rank === 1
                        ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300'
                        : player.rank === 2
                          ? 'bg-slate-300/20 border border-slate-300/40 text-slate-300'
                          : player.rank === 3
                            ? 'bg-amber-700/20 border border-amber-700/40 text-amber-600'
                            : 'bg-slate-950/50 border border-white/5 text-surface-400'
                    }`}>
                      {player.rank}
                    </span>
                    
                    <div className="flex flex-col text-left">
                      <span className={`text-xs font-bold ${isMe ? 'text-purple-300 font-black' : 'text-white'}`}>
                        {player.name} {isMe && <span className="text-[8px] font-normal text-purple-400">(You)</span>}
                      </span>
                      <span className="text-[9px] text-surface-400 font-semibold">
                        {player.prizesClaimed} claims won
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {isWinner && (
                      <span className="text-base" title="Top Ranked">👑</span>
                    )}
                    {player.isOut && (
                      <span className="text-[8px] bg-red-500/20 text-red-400 border border-red-500/20 px-1.5 py-0.5 rounded font-black tracking-wider uppercase">
                        OUT
                      </span>
                    )}
                    <span className="text-xs font-black font-mono text-purple-300">
                      {player.score} pts
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Right Column: User statistics & Game summary stacked vertically */}
        <div className="flex flex-col gap-4 h-full max-h-[360px] lg:max-h-full overflow-y-auto w-full">
          <div className="flex-1 min-h-[170px]">
            <PlayerStats
              rank={userRank}
              prizeCount={userPrizeClaims}
              matchedCount={userMatchedCount}
              completionPercent={completionPercent}
            />
          </div>
          <div className="flex-1 min-h-[150px]">
            <GameSummary
              duration={gameTimer}
              playersCount={leaderboard.length}
              numbersCalled={totalCalledCount}
            />
          </div>
        </div>

      </main>

      {/* Replay action buttons bar */}
      <ActionButtons
        onPlayAgain={handlePlayAgain}
        onReturnHome={handleReturnHome}
        shareDetailsText={shareText}
        isHost={isHost}
      />
    </div>
  );
}
export default GameResultsScreen;
